import { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "./api";
import { useAdminAuth } from "./auth";
import coverSample from "./assets/theme-lab/cover.png";
import dataChartSample from "./assets/theme-lab/data-chart.png";
import multiImageSample from "./assets/theme-lab/multi-image.png";
import textImageSample from "./assets/theme-lab/text-image.png";
import { imageThemesForLab } from "./admin-image-theme-selection";
import { invalidatePublicImageThemeCatalog } from "./image-theme-catalog";
import { debugFrontendEvent } from "./development-debug-log";
import { Badge, Button, EmptyState, LoadingBlock, PageTitle } from "./ui";

type ThemeStatus = "active" | "test" | "gray" | "public" | "disabled" | "deleted";
type ThemeOrigin = "builtin" | "custom" | "builtin_override";
type PageView = "lab" | "history" | "catalog";
type TestContentMode = "enhanced" | "premium" | "faithful";

interface ImageThemeStyleContract {
  colors: { background: string; accent: string; text: string };
  rendererPreferences: { image2StylePrompt?: string; [key: string]: unknown };
  [key: string]: unknown;
}

interface ImageThemePackage {
  displayName: string;
  projectTheme: {
    styleContract: ImageThemeStyleContract;
    [key: string]: unknown;
  };
  selfCheck: {
    image2StylePromptLength: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface ImageTheme {
  code: string;
  displayName: string;
  description: string;
  tags: string[];
  origin: ThemeOrigin;
  status: ThemeStatus;
  revision: number;
  hasCover: boolean;
  package: ImageThemePackage;
  styleContract: ImageThemeStyleContract;
}

interface ThemeJob {
  id: string;
  kind: "extract" | "test_generate";
  status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  progress: number;
  stage: string;
  result?: Record<string, unknown>;
  error?: { message?: string; validationIssues?: string[] };
}

interface TestSample {
  id: string;
  label: string;
  description: string;
  source: string;
  imageUrl: string;
}

interface SampleResult {
  job?: ThemeJob;
  error?: string;
}

interface ThemeHistorySample {
  jobId: string;
  sampleId?: string;
  sampleLabel?: string;
  sampleSource?: string;
  status: ThemeJob["status"];
  progress: number;
  outputUrl?: string;
  outputAvailable: boolean;
  createdAt: string;
  finishedAt?: string;
  retryOfJobId?: string;
}

interface ThemeHistoryRun {
  testRunId: string;
  createdAt: string;
  actor?: string;
  themeCode?: string;
  themeRevision?: number;
  contentMode?: TestContentMode;
  executionContext?: { backendMode?: string; instanceId?: string; service?: string };
  statusSummary: Record<string, number>;
  samples: ThemeHistorySample[];
}

interface ThemeHistoryDetail {
  id: string;
  testRunId: string;
  status: ThemeJob["status"];
  stage: string;
  attempts: number;
  actor?: string;
  themeCode?: string;
  themeRevision?: number;
  sampleId?: string;
  sampleLabel?: string;
  sampleSource?: string;
  contentMode?: TestContentMode;
  instruction?: string;
  retryOfJobId?: string;
  executionContext?: { backendMode?: string; instanceId?: string; service?: string };
  prompt: { stylePrompt?: string; negativePrompt?: string; finalPrompt?: string; sha256?: string; version?: string };
  model?: { id?: string; name?: string; model?: string; provider?: string };
  output: { available: boolean; url?: string; sha256?: string; size?: number; mimeType?: string };
  error?: { code?: string; message?: string };
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
}

const TEST_SAMPLES: TestSample[] = [
  { id: "cover", label: "封面页", description: "大标题、品牌标识与横幅图片", source: "久龄护理院 · 第 1 页", imageUrl: coverSample },
  { id: "text-image", label: "文字图文页", description: "密集说明文字与单张主图", source: "久龄护理院 · 第 2 页", imageUrl: textImageSample },
  { id: "data-chart", label: "数据图表页", description: "核心指标、柱状图、图例与数据说明", source: "AI 大模型综合分析报告 · 第 9 页", imageUrl: dataChartSample },
  { id: "multi-image", label: "多图展示页", description: "多张图片、图注与分组信息", source: "久龄护理院 · 第 4 页", imageUrl: multiImageSample },
];

const TEST_CONTENT_MODES: Array<{
  value: TestContentMode;
  name: string;
  description: string;
  recommended?: boolean;
}> = [
  { value: "enhanced", name: "高质量增强", description: "可优化文字层级与表达，并部分重绘非事实性图片。", recommended: true },
  { value: "premium", name: "素材锁定", description: "锁定原文字和事实图片，允许重新设计版式与装饰。" },
  { value: "faithful", name: "保真美化", description: "尽量保持原构图、阅读顺序和内容关系，只提升视觉品质。" },
];

export function AdminImageThemesPage() {
  const auth = useAdminAuth();
  const canManage = auth.can("models.manage");
  const canTest = canManage || auth.can("themes.test");
  const tester = auth.auth?.session.role === "tester";
  const [themes, setThemes] = useState<ImageTheme[]>();
  const [view, setView] = useState<PageView>("lab");
  const [selectedCode, setSelectedCode] = useState("business-blue");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [localBackend, setLocalBackend] = useState<{ enabled: boolean; instanceId?: string; worker?: { ready?: boolean } }>();

  const loadThemes = useCallback(async (preferredCode?: string) => {
    const data = await adminApi<{ themes: ImageTheme[] }>("/api/admin/image-themes?includeDeleted=true");
    setThemes(data.themes || []);
    if (preferredCode) setSelectedCode(preferredCode);
  }, []);

  useEffect(() => {
    void loadThemes().catch((caught) => {
      setThemes([]);
      setError(message(caught, "风格列表加载失败"));
    });
  }, [loadThemes]);

  useEffect(() => {
    debugFrontendEvent("theme_test_page_loaded", { role: auth.auth?.session.role });
    void adminApi<{ localBackend?: { enabled: boolean; instanceId?: string; worker?: { ready?: boolean } } }>("/api/app/config")
      .then((data) => setLocalBackend(data.localBackend || { enabled: false }))
      .catch(() => setLocalBackend({ enabled: false }));
  }, [auth.auth?.session.role]);

  return <>
    <PageTitle
      eyebrow="Image2 风格实验室"
      title={tester ? "PPT 主题测试" : "PPT 风格调试与发布"}
      description={tester ? "用四类固定原始页面对比 Image2 风格效果，并调试自定义提示词。" : "用四类固定原始页面对比 Image2 风格效果，调试提示词，满意后另存为测试风格并管理发布状态。"}
      action={<div className="theme-page-actions">
        <Button variant={view === "lab" ? "primary" : "secondary"} onClick={() => setView("lab")}>风格实验室</Button>
        <Button variant={view === "history" ? "primary" : "secondary"} onClick={() => setView("history")}>测试历史</Button>
        {!tester && <Button variant={view === "catalog" ? "primary" : "secondary"} onClick={() => setView("catalog")}>风格列表</Button>}
      </div>}
    />
    {localBackend?.enabled && <div className={`theme-runtime-banner${localBackend.worker?.ready ? " ready" : " warning"}`}>
      <strong>本地后端</strong><span>{localBackend.instanceId || "开发实例"}</span><Badge tone={localBackend.worker?.ready ? "success" : "warning"}>{localBackend.worker?.ready ? "Worker 已就绪" : "Worker 未就绪"}</Badge>
    </div>}
    <div aria-live="polite" aria-atomic="true">
      {error && <div className="form-error theme-global-message">{error}</div>}
      {notice && <div className="form-notice theme-global-message">{notice}</div>}
    </div>
    {view === "lab"
      ? <ThemeLab
          themes={themes}
          canTest={canTest}
          canManage={canManage}
          onError={setError}
          onNotice={setNotice}
          onSaved={async (code) => {
            invalidatePublicImageThemeCatalog();
            await loadThemes(code);
            setView("catalog");
            setNotice("新风格已保存为测试状态，可在风格列表中继续管理。");
          }}
        />
      : view === "history"
        ? <ThemeTestHistory themes={themes || []} canViewActor={!tester}/>
        : <ThemeCatalogManager
          themes={themes}
          selectedCode={selectedCode}
          canManage={canManage}
          onSelect={setSelectedCode}
          onError={setError}
          onSaved={async (code, text) => {
            invalidatePublicImageThemeCatalog();
            await loadThemes(code);
            setNotice(text);
          }}
        />}
  </>;
}

function ThemeLab({ themes, canTest, canManage, onError, onNotice, onSaved }: {
  themes?: ImageTheme[];
  canTest: boolean;
  canManage: boolean;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
  onSaved: (code: string) => Promise<void>;
}) {
  const selectableThemes = useMemo(() => imageThemesForLab(themes), [themes]);
  const [selectedSamples, setSelectedSamples] = useState(() => new Set(TEST_SAMPLES.map((sample) => sample.id)));
  const [styleChoice, setStyleChoice] = useState<string>("business-blue");
  const [customBaseCode, setCustomBaseCode] = useState("business-blue");
  const [customPrompt, setCustomPrompt] = useState("");
  const [contentMode, setContentMode] = useState<TestContentMode>("enhanced");
  const [instruction, setInstruction] = useState("");
  const [results, setResults] = useState<Record<string, SampleResult>>({});
  const [running, setRunning] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);

  const selectedTheme = themes?.find((theme) => theme.code === styleChoice);
  const customBase = selectableThemes.find((theme) => theme.code === customBaseCode) || selectableThemes[0];
  const activePrompt = styleChoice === "custom"
    ? customPrompt
    : selectedTheme?.styleContract.rendererPreferences.image2StylePrompt || "";
  const generationThemeCode = styleChoice === "custom" ? customBase?.code : selectedTheme?.code;
  const successfulSamples = TEST_SAMPLES.filter((sample) => results[sample.id]?.job?.status === "succeeded");
  const hasChinesePrompt = /[\u3400-\u9fff]/u.test(activePrompt);

  useEffect(() => {
    if (!selectableThemes.length) return;
    if (!selectableThemes.some((theme) => theme.code === customBaseCode)) setCustomBaseCode(selectableThemes[0].code);
  }, [customBaseCode, selectableThemes]);

  const toggleSample = (sampleId: string) => {
    setSelectedSamples((current) => {
      const next = new Set(current);
      if (next.has(sampleId)) next.delete(sampleId);
      else next.add(sampleId);
      return next;
    });
  };

  const chooseCustom = () => {
    const source = selectedTheme || customBase || selectableThemes[0];
    setStyleChoice("custom");
    if (source) {
      setCustomBaseCode(source.code);
      setCustomPrompt((current) => current || source.styleContract.rendererPreferences.image2StylePrompt || "");
    }
    setResults({});
  };

  const copySelectedPrompt = async () => {
    const prompt = selectedTheme?.styleContract.rendererPreferences.image2StylePrompt || "";
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      onError("");
      onNotice(`已复制“${selectedTheme?.displayName}”的原版提示词。`);
    } catch {
      onNotice("");
      onError("提示词复制失败，请在文本框中手动选择并复制。");
    }
  };

  const customizeSelectedPrompt = () => {
    if (!selectedTheme) return;
    setCustomBaseCode(selectedTheme.code);
    setCustomPrompt(selectedTheme.styleContract.rendererPreferences.image2StylePrompt || "");
    setStyleChoice("custom");
    setResults({});
    onError("");
    onNotice(`已载入“${selectedTheme.displayName}”的提示词，可继续编辑调试。`);
  };

  const generateOne = async (sample: TestSample, testRunId = crypto.randomUUID(), retryOfJobId?: string) => {
    if (!generationThemeCode) return;
    debugFrontendEvent(retryOfJobId ? "theme_test_manual_retry_started" : "theme_test_sample_submission_started", {
      testRunId,
      retryOfJobId,
      sampleId: sample.id,
      themeCode: generationThemeCode,
      contentMode,
    });
    setResults((current) => ({ ...current, [sample.id]: { job: {
      id: "", kind: "test_generate", status: "queued", progress: 0, stage: "正在提交",
    } } }));
    try {
      const imageDataUrl = await imageUrlToDataUrl(sample.imageUrl);
      const data = await adminApi<{ job: ThemeJob }>(
        `/api/admin/image-themes/${encodeURIComponent(generationThemeCode)}/test-generations`,
        {
          method: "POST",
          body: JSON.stringify({
            testRunId,
            sampleId: sample.id,
            sampleLabel: sample.label,
            sampleSource: sample.source,
            retryOfJobId,
            imageDataUrl,
            text: instruction.trim() || undefined,
            stylePrompt: styleChoice === "custom" ? customPrompt.trim() : undefined,
            contentMode,
          }),
        },
      );
      setResults((current) => ({ ...current, [sample.id]: { job: data.job } }));
      const completed = await pollJob(data.job.id, (job) => {
        setResults((current) => ({ ...current, [sample.id]: { job } }));
      });
      if (completed.status === "failed") {
        setResults((current) => ({ ...current, [sample.id]: { job: completed, error: jobError(completed) } }));
      }
      debugFrontendEvent(completed.status === "succeeded" ? "theme_test_sample_completed" : "theme_test_sample_failed", {
        testRunId,
        jobId: completed.id,
        sampleId: sample.id,
        status: completed.status,
      });
    } catch (caught) {
      setResults((current) => ({ ...current, [sample.id]: { error: message(caught, "生成失败") } }));
      debugFrontendEvent("theme_test_sample_failed", { testRunId, sampleId: sample.id, error: message(caught, "生成失败") });
    }
  };

  const generateSelected = async () => {
    const samples = TEST_SAMPLES.filter((sample) => selectedSamples.has(sample.id));
    if (!samples.length || !generationThemeCode || !activePrompt.trim()) return;
    setRunning(true);
    setResults({});
    onError("");
    onNotice("");
    const testRunId = crypto.randomUUID();
    debugFrontendEvent("theme_test_run_started", {
      testRunId,
      themeCode: generationThemeCode,
      sampleIds: samples.map((sample) => sample.id),
      contentMode,
      promptLength: activePrompt.length,
    });
    try {
      await Promise.all(samples.map((sample) => generateOne(sample, testRunId)));
    } finally {
      setRunning(false);
    }
  };

  if (!themes) return <section className="theme-lab-shell"><LoadingBlock/></section>;

  return <div className="theme-lab-shell">
    <section className="theme-lab-section">
      <SectionHeading step="01" title="选择原始测试页" description="固定基准页来自本地历史 PPT，可选择 1–4 页同时测试。"/>
      <div className="theme-sample-grid">
        {TEST_SAMPLES.map((sample) => {
          const selected = selectedSamples.has(sample.id);
          return <button
            type="button"
            key={sample.id}
            className={`theme-sample-card${selected ? " selected" : ""}`}
            aria-pressed={selected}
            onClick={() => toggleSample(sample.id)}
          >
            <span className="theme-sample-check" aria-hidden="true">{selected ? "✓" : ""}</span>
            <img src={sample.imageUrl} alt={`${sample.label}原始页面`}/>
            <span className="theme-sample-meta"><strong>{sample.label}</strong><small>{sample.description}</small><em>{sample.source}</em></span>
          </button>;
        })}
      </div>
    </section>

    <section className="theme-lab-section">
      <SectionHeading step="02" title="选择 Image2 风格" description="展示所有未删除风格；选择自定义后可直接编辑完整风格提示词。"/>
      <div className="theme-style-strip">
        {selectableThemes.map((theme) => <button
          type="button"
          key={theme.code}
          className={`theme-style-card${styleChoice === theme.code ? " selected" : ""}`}
          aria-pressed={styleChoice === theme.code}
          onClick={() => { setStyleChoice(theme.code); setResults({}); }}
        >
          <span className="theme-style-swatch" style={{ background: `linear-gradient(135deg, ${theme.styleContract.colors.background}, ${theme.styleContract.colors.accent})` }}/>
          <span><strong>{theme.displayName}</strong><small>{theme.description}</small></span>
        </button>)}
        <button
          type="button"
          className={`theme-style-card custom${styleChoice === "custom" ? " selected" : ""}`}
          aria-pressed={styleChoice === "custom"}
          onClick={chooseCustom}
        >
          <span className="theme-style-swatch">Aa</span>
          <span><strong>自定义提示词</strong><small>基于任一风格继续调试</small></span>
        </button>
      </div>

      {styleChoice !== "custom" && selectedTheme && <div className="theme-fixed-prompt">
        <div className="theme-fixed-prompt-head">
          <div>
            <span>原版 Image2 风格提示词</span>
            <strong>{selectedTheme.displayName}</strong>
          </div>
          <div className="theme-fixed-prompt-actions">
            <Button variant="secondary" onClick={() => void copySelectedPrompt()}>复制提示词</Button>
            <Button variant="secondary" onClick={customizeSelectedPrompt}>基于此提示词自定义</Button>
          </div>
        </div>
        <textarea
          readOnly
          value={selectedTheme.styleContract.rendererPreferences.image2StylePrompt || ""}
          spellCheck={false}
          aria-label={`${selectedTheme.displayName}原版 Image2 风格提示词`}
        />
        <div className="theme-prompt-foot">
          <span>这是该固定风格实际传给 Image2 的完整提示词，可复制参考或载入后修改。</span>
          <span>{selectedTheme.styleContract.rendererPreferences.image2StylePrompt?.length || 0} / 4000</span>
        </div>
      </div>}

      {styleChoice === "custom" && <div className="theme-custom-prompt">
        <div className="theme-custom-prompt-toolbar">
          <label htmlFor="theme-custom-base">主题包基础</label>
          <select id="theme-custom-base" value={customBaseCode} disabled={running} onChange={(event) => { setCustomBaseCode(event.target.value); setResults({}); }}>
            {selectableThemes.map((theme) => <option key={theme.code} value={theme.code}>{theme.displayName}</option>)}
          </select>
          <Button variant="secondary" disabled={running || !customBase} onClick={() => { setCustomPrompt(customBase?.styleContract.rendererPreferences.image2StylePrompt || ""); setResults({}); }}>载入基础提示词</Button>
        </div>
        <label htmlFor="theme-custom-prompt">Image2 风格提示词</label>
        <textarea
          id="theme-custom-prompt"
          value={customPrompt}
          maxLength={4_000}
          disabled={running}
          spellCheck={false}
          aria-invalid={hasChinesePrompt}
          onChange={(event) => { setCustomPrompt(event.target.value); setResults({}); }}
        />
        <div className="theme-prompt-foot">
          <span className={hasChinesePrompt ? "invalid" : ""}>{hasChinesePrompt ? "当前主题包规范要求提示词使用英文" : "提示词将原样传给 Image2 测试任务"}</span>
          <span>{customPrompt.length} / 4000</span>
        </div>
      </div>}

      <fieldset className="theme-content-mode" disabled={running}>
        <div className="theme-content-mode-head">
          <div><span>原始内容修改程度</span><strong>选择美化时允许 Image2 调整内容与版式的范围</strong></div>
          <small>此选项会随测试任务一起提交</small>
        </div>
        <div className="theme-content-mode-options">
          {TEST_CONTENT_MODES.map((mode) => <button
            type="button"
            key={mode.value}
            className={contentMode === mode.value ? "selected" : ""}
            aria-pressed={contentMode === mode.value}
            onClick={() => { setContentMode(mode.value); setResults({}); }}
          >
            <span><strong>{mode.name}</strong>{mode.recommended && <b>推荐</b>}</span>
            <small>{mode.description}</small>
          </button>)}
        </div>
      </fieldset>

      <div className="theme-test-controls">
        <label htmlFor="theme-test-instruction"><span>本轮附加指令（可选）</span><input id="theme-test-instruction" value={instruction} maxLength={2_000} disabled={running} placeholder="例如：保留所有数字和中文文字，不改变数据含义" onChange={(event) => setInstruction(event.target.value)}/></label>
        <div>
          <span>已选 {selectedSamples.size} 页 · 将生成 {selectedSamples.size} 张测试图</span>
          <Button
            disabled={!canTest || running || !selectedSamples.size || !generationThemeCode || !activePrompt.trim() || hasChinesePrompt}
            onClick={() => void generateSelected()}
          >{running ? "测试生成中…" : "开始测试"}</Button>
        </div>
      </div>
    </section>

    <section className="theme-lab-section">
      <SectionHeading step="03" title="查看美化结果" description="原始页与生成结果并排展示；单页失败不会影响其他页面，可独立重试。"/>
      {!Object.keys(results).length
        ? <EmptyState title="尚未生成测试结果" description="选择页面与风格后点击“开始测试”，结果会显示在这里。"/>
        : <div className="theme-result-list">
            {TEST_SAMPLES.filter((sample) => selectedSamples.has(sample.id) || results[sample.id]).map((sample) => {
              const result = results[sample.id];
              return <article className="theme-result-card" key={sample.id}>
                <div className="theme-result-title">
                  <div><strong>{sample.label}</strong><span>{sample.source}</span></div>
                  {result?.job && <Badge tone={result.job.status === "succeeded" ? "success" : result.job.status === "failed" ? "danger" : "warning"}>{jobStatusLabel(result.job.status)}</Badge>}
                </div>
                <div className="theme-result-compare">
                  <figure><span>原始页面</span><img src={sample.imageUrl} alt={`${sample.label}原始页面`}/></figure>
                  <figure className={!result?.job || result.job.status !== "succeeded" ? "pending" : ""}>
                    <span>美化结果</span>
                    {result?.job?.status === "succeeded"
                      ? <img src={jobOutputUrl(result.job)} alt={`${sample.label}美化结果`}/>
                      : <div className="theme-result-placeholder">
                          <strong>{result?.error || result?.job?.stage || "等待生成"}</strong>
                          {result?.job && <progress max={100} value={result.job.progress}/>}
                        </div>}
                  </figure>
                </div>
                <div className="theme-result-actions">
                  <span>{result?.job?.status === "succeeded" ? "可选为新风格封面" : result?.error || result?.job?.stage}</span>
                  <Button variant="secondary" disabled={!canTest || running} onClick={() => void generateOne(sample, crypto.randomUUID(), result?.job?.id)}>重新生成此页</Button>
                </div>
              </article>;
            })}
          </div>}
      {canManage && successfulSamples.length > 0 && <div className="theme-save-banner">
        <div><strong>提示词效果满意？</strong><span>将当前提示词和一张生成结果保存为新的测试风格。</span></div>
        <Button disabled={!canManage || running} onClick={() => setSaveOpen(true)}>另存为新风格</Button>
      </div>}
    </section>

    {canManage && saveOpen && <SaveThemeDialog
      prompt={activePrompt}
      sourceThemeCode={generationThemeCode || ""}
      sourceName={styleChoice === "custom" ? "自定义提示词" : selectedTheme?.displayName || ""}
      successfulSamples={successfulSamples}
      results={results}
      onClose={() => setSaveOpen(false)}
      onError={onError}
      onSaved={onSaved}
    />}
  </div>;
}

function ThemeTestHistory({ themes, canViewActor }: { themes: ImageTheme[]; canViewActor: boolean }) {
  const [runs, setRuns] = useState<ThemeHistoryRun[]>();
  const [cursor, setCursor] = useState<string>();
  const [themeCode, setThemeCode] = useState("");
  const [status, setStatus] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<ThemeHistoryDetail>();
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async (reset: boolean, nextCursor?: string) => {
    if (!reset) setLoadingMore(true);
    setError("");
    try {
      const query = new URLSearchParams({ limit: "20" });
      if (themeCode) query.set("themeCode", themeCode);
      if (status) query.set("status", status);
      if (nextCursor) query.set("cursor", nextCursor);
      const data = await adminApi<{ runs: ThemeHistoryRun[]; nextCursor?: string }>(`/api/admin/image-theme-test-history?${query}`);
      setRuns((current) => reset ? data.runs : [...(current || []), ...data.runs]);
      setCursor(data.nextCursor);
      debugFrontendEvent("theme_test_history_loaded", { reset, resultCount: data.runs.length, themeCode, status });
    } catch (caught) {
      setError(message(caught, "测试历史加载失败"));
      if (reset) setRuns([]);
    } finally {
      setLoadingMore(false);
    }
  }, [status, themeCode]);

  useEffect(() => { void load(true); }, [load]);

  const openDetail = async (jobId: string) => {
    setDetailLoading(true);
    setError("");
    try {
      const data = await adminApi<{ history: ThemeHistoryDetail }>(`/api/admin/image-theme-test-history/${encodeURIComponent(jobId)}`);
      setDetail(data.history);
      debugFrontendEvent("theme_test_history_detail_opened", { jobId, testRunId: data.history.testRunId });
    } catch (caught) {
      setError(message(caught, "测试历史详情加载失败"));
    } finally {
      setDetailLoading(false);
    }
  };

  return <section className="theme-history-shell">
    <div className="theme-history-toolbar">
      <div><h2>测试生成历史</h2><p>结果图片和当时实际使用的生图提示词均持久化在本地。</p></div>
      <div className="theme-history-filters">
        <label><span>主题</span><select value={themeCode} onChange={(event) => setThemeCode(event.target.value)}><option value="">全部主题</option>{themes.map((theme) => <option key={theme.code} value={theme.code}>{theme.displayName}</option>)}</select></label>
        <label><span>状态</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">全部状态</option><option value="succeeded">已完成</option><option value="failed">失败</option><option value="running">生成中</option><option value="queued">排队中</option><option value="cancelled">已取消</option></select></label>
      </div>
    </div>
    {error && <div className="form-error">{error}</div>}
    {!runs ? <LoadingBlock/> : runs.length === 0
      ? <EmptyState title="暂无测试历史" description="完成一次主题测试后，生成结果会显示在这里。"/>
      : <div className="theme-history-list">{runs.map((run) => {
          const theme = themes.find((candidate) => candidate.code === run.themeCode);
          return <article className="theme-history-card" key={run.testRunId}>
            <div className="theme-history-card-head">
              <div><strong>{theme?.displayName || run.themeCode || "未知主题"}</strong><span>{formatHistoryTime(run.createdAt)} · {contentModeLabel(run.contentMode)}</span></div>
              <div>{run.executionContext?.backendMode === "local-backend-only" && <Badge tone="brand">本地 API</Badge>}<Badge tone={run.statusSummary.failed ? "danger" : run.statusSummary.succeeded === run.samples.length ? "success" : "warning"}>{historyRunStatus(run)}</Badge></div>
            </div>
            <div className="theme-history-meta"><span>批次 {run.testRunId}</span>{canViewActor && run.actor && <span>操作人 {run.actor}</span>}<span>主题版本 {run.themeRevision || "-"}</span></div>
            <div className="theme-history-samples">{run.samples.map((sample) => <button type="button" key={sample.jobId} onClick={() => void openDetail(sample.jobId)}>
              {sample.outputAvailable && sample.outputUrl ? <img src={sample.outputUrl} alt={`${sample.sampleLabel || "测试页"}生成结果`}/> : <span className="theme-history-placeholder">{jobStatusLabel(sample.status)}</span>}
              <strong>{sample.sampleLabel || "测试页面"}</strong><small>{sample.sampleSource || sample.jobId}</small>
            </button>)}</div>
          </article>;
        })}</div>}
    {cursor && <div className="theme-history-more"><Button variant="secondary" disabled={loadingMore} onClick={() => void load(false, cursor)}>{loadingMore ? "加载中…" : "加载更多"}</Button></div>}
    {detailLoading && <div className="theme-history-detail-loading"><LoadingBlock/></div>}
    {detail && <ThemeHistoryDetailDialog detail={detail} onClose={() => setDetail(undefined)}/>}
  </section>;
}

function ThemeHistoryDetailDialog({ detail, onClose }: { detail: ThemeHistoryDetail; onClose: () => void }) {
  const sample = TEST_SAMPLES.find((candidate) => candidate.id === detail.sampleId);
  const copyPrompt = async (value: string | undefined) => {
    if (value) await navigator.clipboard.writeText(value);
  };
  return <div className="theme-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="theme-history-detail" role="dialog" aria-modal="true" aria-labelledby="theme-history-detail-title">
      <div className="theme-dialog-head"><div><span className="eyebrow">测试历史</span><h2 id="theme-history-detail-title">{detail.sampleLabel || "主题测试详情"}</h2><p>{formatHistoryTime(detail.createdAt)} · {jobStatusLabel(detail.status)} · 尝试 {detail.attempts} 次</p></div><button type="button" aria-label="关闭" onClick={onClose}>×</button></div>
      <div className="theme-history-compare">
        <figure><span>原始页面</span>{sample ? <img src={sample.imageUrl} alt={`${sample.label}原始页面`}/> : <div>原始图片已清理</div>}</figure>
        <figure><span>生成结果</span>{detail.output.available && detail.output.url ? <img src={detail.output.url} alt="生成结果"/> : <div>结果图片不可用</div>}</figure>
      </div>
      <dl className="theme-history-detail-meta"><div><dt>主题</dt><dd>{detail.themeCode} · v{detail.themeRevision || "-"}</dd></div><div><dt>内容模式</dt><dd>{contentModeLabel(detail.contentMode)}</dd></div><div><dt>模型</dt><dd>{detail.model?.name || detail.model?.model || "-"}</dd></div><div><dt>执行位置</dt><dd>{detail.executionContext?.backendMode === "local-backend-only" ? `本地 API · ${detail.executionContext.instanceId || "开发实例"}` : detail.executionContext?.service || "API"}</dd></div><div><dt>耗时</dt><dd>{detail.durationMs == null ? "-" : `${Math.round(detail.durationMs / 100) / 10} 秒`}</dd></div><div><dt>重试来源</dt><dd>{detail.retryOfJobId || "-"}</dd></div></dl>
      {detail.instruction && <section className="theme-history-prompt"><div><strong>附加指令</strong></div><pre>{detail.instruction}</pre></section>}
      <section className="theme-history-prompt"><div><strong>Image2 风格提示词</strong><Button variant="secondary" onClick={() => void copyPrompt(detail.prompt.stylePrompt)}>复制</Button></div><pre>{detail.prompt.stylePrompt || "未保存"}</pre></section>
      <section className="theme-history-prompt"><div><strong>最终生图提示词</strong><Button variant="secondary" onClick={() => void copyPrompt(detail.prompt.finalPrompt)}>复制</Button></div><pre>{detail.prompt.finalPrompt || "未保存"}</pre><small>SHA-256：{detail.prompt.sha256 || "-"}</small></section>
      {detail.error?.message && <div className="form-error">{detail.error.code}: {detail.error.message}</div>}
    </section>
  </div>;
}

function historyRunStatus(run: ThemeHistoryRun): string {
  if (run.statusSummary.running || run.statusSummary.queued) return "生成中";
  if (run.statusSummary.failed && run.statusSummary.succeeded) return "部分失败";
  if (run.statusSummary.failed) return "失败";
  if (run.statusSummary.cancelled) return "已取消";
  return "已完成";
}

function contentModeLabel(mode?: TestContentMode): string {
  return TEST_CONTENT_MODES.find((candidate) => candidate.value === mode)?.name || mode || "-";
}

function formatHistoryTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("zh-CN", { hour12: false });
}

function ThemeCatalogManager({ themes, selectedCode, canManage, onSelect, onError, onSaved }: {
  themes?: ImageTheme[];
  selectedCode: string;
  canManage: boolean;
  onSelect: (code: string) => void;
  onError: (message: string) => void;
  onSaved: (code: string, notice: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [stylePrompt, setStylePrompt] = useState("");
  const [coverDataUrl, setCoverDataUrl] = useState("");
  const [coverFileName, setCoverFileName] = useState("");
  const selected = themes?.find((theme) => theme.code === selectedCode) || themes?.[0];

  useEffect(() => {
    if (!selectedCode && themes?.[0]) onSelect(themes[0].code);
  }, [onSelect, selectedCode, themes]);

  useEffect(() => {
    setEditing(false);
    setTitle(selected?.displayName || "");
    setDescription(selected?.description || "");
    setStylePrompt(selected?.styleContract.rendererPreferences.image2StylePrompt || "");
    setCoverDataUrl("");
    setCoverFileName("");
  }, [selected?.code, selected?.revision]);

  if (!themes) return <div className="theme-admin-layout"><aside className="theme-catalog-panel"><LoadingBlock/></aside></div>;

  const beginEdit = () => {
    if (!selected) return;
    onError("");
    setTitle(selected.displayName);
    setDescription(selected.description);
    setStylePrompt(selected.styleContract.rendererPreferences.image2StylePrompt || "");
    setCoverDataUrl("");
    setCoverFileName("");
    setEditing(true);
  };

  const cancelEdit = () => {
    if (busy) return;
    setEditing(false);
    setCoverDataUrl("");
    setCoverFileName("");
    onError("");
  };

  const chooseCover = async (file?: File) => {
    if (!file) return;
    onError("");
    if (!(["image/png", "image/jpeg", "image/webp"] as string[]).includes(file.type)) {
      onError("风格图片仅支持 PNG、JPEG 或 WebP 格式。");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      onError("风格图片不能超过 10 MB。");
      return;
    }
    try {
      setCoverDataUrl(await fileToDataUrl(file));
      setCoverFileName(file.name);
    } catch (caught) {
      onError(message(caught, "风格图片读取失败"));
    }
  };

  const saveEdit = async () => {
    if (!selected) return;
    const nextTitle = title.trim();
    const nextDescription = description.trim();
    const nextPrompt = stylePrompt.trim();
    if (!nextTitle || !nextDescription || !nextPrompt) {
      onError("请填写风格标题、风格说明和完整提示词。");
      return;
    }
    if (/[㐀-鿿]/u.test(nextPrompt)) {
      onError("Image2 风格提示词必须使用英文。");
      return;
    }
    const pkg = structuredClone(selected.package);
    pkg.displayName = nextTitle;
    pkg.description = nextDescription;
    pkg.projectTheme.styleContract.rendererPreferences.image2StylePrompt = nextPrompt;
    pkg.selfCheck.image2StylePromptLength = nextPrompt.length;
    setBusy(true);
    onError("");
    try {
      await adminApi(`/api/admin/image-themes/${encodeURIComponent(selected.code)}`, {
        method: "PATCH",
        body: JSON.stringify({
          expectedRevision: selected.revision,
          package: pkg,
          ...(coverDataUrl ? { coverDataUrl } : {}),
        }),
      });
      setEditing(false);
      setCoverDataUrl("");
      setCoverFileName("");
      await onSaved(selected.code, "风格已保存，用户端新建 PPT 的风格列表将同步更新。");
    } catch (caught) {
      onError(message(caught, "风格保存失败"));
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (status: Exclude<ThemeStatus, "deleted">) => {
    if (!selected) return;
    setBusy(true);
    onError("");
    try {
      await adminApi(`/api/admin/image-themes/${encodeURIComponent(selected.code)}`, {
        method: "PATCH",
        body: JSON.stringify({ expectedRevision: selected.revision, status, package: selected.package }),
      });
      await onSaved(selected.code, `风格状态已更新为“${themeStatusLabel(status)}”。`);
    } catch (caught) {
      onError(message(caught, "状态更新失败"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!selected || selected.origin !== "custom" || !window.confirm(`确认删除风格“${selected.displayName}”？`)) return;
    setBusy(true);
    onError("");
    try {
      await adminApi(`/api/admin/image-themes/${encodeURIComponent(selected.code)}`, {
        method: "DELETE",
        body: JSON.stringify({ expectedRevision: selected.revision }),
      });
      await onSaved(selected.code, "风格已软删除，历史目录和备份仍保留。");
    } catch (caught) {
      onError(message(caught, "删除风格失败"));
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!selected) return;
    setBusy(true);
    onError("");
    try {
      await adminApi(`/api/admin/image-themes/${encodeURIComponent(selected.code)}/restore`, {
        method: "POST",
        body: JSON.stringify({ expectedRevision: selected.revision }),
      });
      await onSaved(selected.code, selected.origin === "builtin_override" ? "已恢复内置风格默认值。" : "风格已恢复为测试状态。");
    } catch (caught) {
      onError(message(caught, "恢复风格失败"));
    } finally {
      setBusy(false);
    }
  };

  return <div className="theme-admin-layout">
    <aside className="theme-catalog-panel">
      <div className="theme-panel-head"><div><h2>全部风格</h2><p>{themes.length} 个内置或自定义风格</p></div></div>
      <div className="theme-catalog-list">
        {themes.map((theme) => <button type="button" key={theme.code} className={selected?.code === theme.code ? "selected" : ""} onClick={() => onSelect(theme.code)}>
          {theme.hasCover
            ? <img className="theme-cover-chip" src={`/api/admin/image-themes/${encodeURIComponent(theme.code)}/cover?v=${theme.revision}`} alt=""/>
            : <span className="theme-color-chip" style={{ background: `linear-gradient(135deg, ${theme.styleContract.colors.background}, ${theme.styleContract.colors.accent})` }}/>}
          <span><strong>{theme.displayName}</strong><small>{theme.code}</small></span>
          <Badge tone={themeStatusTone(theme.status)}>{themeStatusLabel(theme.status)}</Badge>
        </button>)}
      </div>
    </aside>
    {!selected
      ? <section className="theme-work-panel"><EmptyState title="暂无风格" description="在实验室完成测试后可另存为新风格。"/></section>
      : <section className="theme-work-panel theme-release-panel">
          <div className="theme-panel-head theme-editor-head">
            <div><span className="eyebrow">风格详情</span><h2>{selected.displayName}</h2><p>{selected.code} · revision {selected.revision}</p></div>
            <div className="theme-editor-head-actions"><span><Badge tone={selected.origin === "builtin" ? "brand" : "neutral"}>{originLabel(selected.origin)}</Badge><Badge tone={themeStatusTone(selected.status)}>{themeStatusLabel(selected.status)}</Badge></span>{!editing && <Button variant="secondary" disabled={!canManage || busy || selected.status === "deleted"} onClick={beginEdit}>编辑风格</Button>}</div>
          </div>
          {editing
            ? <div className="theme-catalog-edit-form">
                <div className="theme-catalog-cover-editor">
                  {coverDataUrl || selected.hasCover
                    ? <img src={coverDataUrl || `/api/admin/image-themes/${encodeURIComponent(selected.code)}/cover?v=${selected.revision}`} alt="风格图片预览"/>
                    : <div className="theme-release-gradient" style={{ background: `linear-gradient(135deg, ${selected.styleContract.colors.background}, ${selected.styleContract.colors.accent})` }}><span>{title || selected.displayName}</span></div>}
                  <label className={busy ? "disabled" : ""}><span>{coverFileName || "更换图片"}</span><small>PNG / JPEG / WebP，最大 10 MB</small><input type="file" accept="image/png,image/jpeg,image/webp" disabled={busy} onChange={(event) => void chooseCover(event.target.files?.[0])}/></label>
                </div>
                <div className="theme-catalog-edit-fields">
                  <label htmlFor="theme-catalog-edit-title"><span>风格标题</span><input id="theme-catalog-edit-title" autoFocus value={title} maxLength={80} disabled={busy} onChange={(event) => setTitle(event.target.value)}/><small>{title.trim().length} / 80</small></label>
                  <label htmlFor="theme-catalog-edit-description"><span>风格说明</span><textarea id="theme-catalog-edit-description" value={description} maxLength={500} disabled={busy} onChange={(event) => setDescription(event.target.value)}/><small>{description.trim().length} / 500</small></label>
                  <label htmlFor="theme-catalog-edit-prompt"><span>Image2 风格提示词</span><textarea id="theme-catalog-edit-prompt" value={stylePrompt} maxLength={4000} disabled={busy} onChange={(event) => setStylePrompt(event.target.value)}/><small className={/[\u3400-\u9fff]/u.test(stylePrompt) ? "error" : ""}>{/[\u3400-\u9fff]/u.test(stylePrompt) ? "提示词必须使用英文 · " : ""}{stylePrompt.trim().length} / 4000</small></label>
                  <div className="theme-catalog-edit-actions"><Button variant="secondary" disabled={busy} onClick={cancelEdit}>取消</Button><Button disabled={busy || !title.trim() || !description.trim() || !stylePrompt.trim() || /[\u3400-\u9fff]/u.test(stylePrompt)} onClick={() => void saveEdit()}>{busy ? "保存中…" : "保存"}</Button></div>
                </div>
              </div>
            : <>
              <div className="theme-release-overview">
                {selected.hasCover
                  ? <img src={`/api/admin/image-themes/${encodeURIComponent(selected.code)}/cover?v=${selected.revision}`} alt={`${selected.displayName}封面`}/>
                  : <div className="theme-release-gradient" style={{ background: `linear-gradient(135deg, ${selected.styleContract.colors.background}, ${selected.styleContract.colors.accent})` }}><span>{selected.displayName}</span></div>}
                <div>
                  <h3>{selected.description}</h3>
                  <p>{selected.tags.join(" · ")}</p>
                  <dl>
                    <div><dt>来源</dt><dd>{originLabel(selected.origin)}</dd></div>
                    <div><dt>提示词</dt><dd>{selected.styleContract.rendererPreferences.image2StylePrompt?.length || 0} 字符</dd></div>
                    <div><dt>当前状态</dt><dd>{themeStatusLabel(selected.status)}</dd></div>
                  </dl>
                </div>
              </div>
              <div className="theme-lifecycle">
                <h3>发布状态</h3>
                <p>测试 → 灰度 → 上架；下架后仅管理员可见和重新测试。</p>
                <div className="theme-lifecycle-actions">
                  {(["test", "gray", "public", "disabled"] as const).map((status) => <Button
                    key={status}
                    variant={normalizeStatus(selected.status) === status ? "primary" : "secondary"}
                    disabled={!canManage || busy || selected.status === "deleted" || normalizeStatus(selected.status) === status}
                    onClick={() => void changeStatus(status)}
                  >{themeStatusLabel(status)}</Button>)}
                  {selected.status === "deleted"
                    ? <Button disabled={!canManage || busy} onClick={() => void restore()}>恢复风格</Button>
                    : <>
                        {selected.origin === "builtin_override" && <Button variant="secondary" disabled={!canManage || busy} onClick={() => void restore()}>恢复内置默认</Button>}
                        {selected.origin === "custom" && <Button variant="danger" disabled={!canManage || busy} onClick={() => void remove()}>删除风格</Button>}
                      </>}
                </div>
              </div>
              <details className="theme-prompt-details">
                <summary>查看完整 Image2 风格提示词</summary>
                <pre>{selected.styleContract.rendererPreferences.image2StylePrompt || "未配置"}</pre>
              </details>
            </>}
        </section>}
  </div>;
}

function SaveThemeDialog({ prompt, sourceThemeCode, sourceName, successfulSamples, results, onClose, onError, onSaved }: {
  prompt: string;
  sourceThemeCode: string;
  sourceName: string;
  successfulSamples: TestSample[];
  results: Record<string, SampleResult>;
  onClose: () => void;
  onError: (message: string) => void;
  onSaved: (code: string) => Promise<void>;
}) {
  const [title, setTitle] = useState(`${sourceName || "Image2"} 调试版`);
  const [description, setDescription] = useState("由风格实验室验证并保存的 Image2 提示词风格。");
  const [coverSampleId, setCoverSampleId] = useState(successfulSamples[0]?.id || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [busy, onClose]);

  const save = async () => {
    const coverJob = results[coverSampleId]?.job;
    if (!coverJob?.id) return;
    setBusy(true);
    onError("");
    try {
      const data = await adminApi<{ theme: ImageTheme }>("/api/admin/image-themes", {
        method: "POST",
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          sourceThemeCode,
          stylePrompt: prompt.trim(),
          coverJobId: coverJob.id,
        }),
      });
      debugFrontendEvent("theme_test_theme_saved", { themeCode: data.theme.code, sourceThemeCode, coverJobId: coverJob.id });
      await onSaved(data.theme.code);
      onClose();
    } catch (caught) {
      debugFrontendEvent("theme_test_theme_save_failed", { sourceThemeCode, coverJobId: coverJob.id, error: message(caught, "新风格保存失败") });
      onError(message(caught, "新风格保存失败"));
    } finally {
      setBusy(false);
    }
  };

  return <div className="theme-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <section className="theme-save-dialog" role="dialog" aria-modal="true" aria-labelledby="theme-save-title">
      <div className="theme-dialog-head"><div><span className="eyebrow">另存为</span><h2 id="theme-save-title">保存新的测试风格</h2><p>新风格默认进入“测试”状态，不会直接对普通用户上架。</p></div><button type="button" aria-label="关闭" disabled={busy} onClick={onClose}>×</button></div>
      <div className="theme-save-form">
        <label htmlFor="theme-save-name"><span>风格标题</span><input id="theme-save-name" autoFocus value={title} maxLength={80} disabled={busy} onChange={(event) => setTitle(event.target.value)}/></label>
        <label htmlFor="theme-save-description"><span>风格说明</span><textarea id="theme-save-description" value={description} maxLength={500} disabled={busy} onChange={(event) => setDescription(event.target.value)}/></label>
        <fieldset><legend>选择封面</legend><div className="theme-cover-picker">
          {successfulSamples.map((sample) => {
            const job = results[sample.id]?.job;
            if (!job) return null;
            return <label key={sample.id} className={coverSampleId === sample.id ? "selected" : ""}>
              <input type="radio" name="theme-cover" value={sample.id} checked={coverSampleId === sample.id} disabled={busy} onChange={() => setCoverSampleId(sample.id)}/>
              <img src={jobOutputUrl(job)} alt={`${sample.label}生成结果`}/><span>{sample.label}</span>
            </label>;
          })}
        </div></fieldset>
      </div>
      <div className="theme-dialog-actions"><Button variant="secondary" disabled={busy} onClick={onClose}>取消</Button><Button disabled={busy || !title.trim() || !description.trim() || !coverSampleId} onClick={() => void save()}>{busy ? "保存中…" : "保存为测试风格"}</Button></div>
    </section>
  </div>;
}

function SectionHeading({ step, title, description }: { step: string; title: string; description: string }) {
  return <div className="theme-section-heading"><span>{step}</span><div><h2>{title}</h2><p>{description}</p></div></div>;
}

async function pollJob(id: string, update: (job: ThemeJob) => void): Promise<ThemeJob> {
  for (;;) {
    await delay(1_400);
    const data = await adminApi<{ job: ThemeJob }>(`/api/admin/image-theme-jobs/${encodeURIComponent(id)}`);
    update(data.job);
    if (["succeeded", "failed", "cancelled"].includes(data.job.status)) return data.job;
  }
}

async function imageUrlToDataUrl(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) throw new Error("原始测试页读取失败");
  const blob = await response.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("原始测试页编码失败"));
    reader.readAsDataURL(blob);
  });
}

async function fileToDataUrl(file: File): Promise<string> {
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("图片读取失败"));
    reader.readAsDataURL(file);
  });
}

function jobOutputUrl(job: ThemeJob): string {
  return `/api/admin/image-theme-jobs/${encodeURIComponent(job.id)}/output?v=${String(job.result?.sha256 || job.id)}`;
}

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function message(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function jobError(job: ThemeJob): string {
  return [job.error?.message || "任务失败", ...(job.error?.validationIssues || [])].join("；");
}

function jobStatusLabel(status: ThemeJob["status"]): string {
  return ({ queued: "排队中", running: "生成中", succeeded: "已完成", failed: "失败", cancelled: "已取消" } as const)[status];
}

function normalizeStatus(status: ThemeStatus): Exclude<ThemeStatus, "active" | "deleted"> | "public" {
  return status === "active" ? "public" : status === "deleted" ? "disabled" : status;
}

function themeStatusLabel(status: ThemeStatus): string {
  return ({ active: "上架", test: "测试", gray: "灰度", public: "上架", disabled: "下架", deleted: "已删除" } as const)[status];
}

function themeStatusTone(status: ThemeStatus): "success" | "warning" | "danger" | "brand" | "neutral" {
  if (status === "active" || status === "public") return "success";
  if (status === "gray") return "brand";
  if (status === "deleted") return "danger";
  return "warning";
}

function originLabel(origin: ThemeOrigin): string {
  return ({ builtin: "内置风格", custom: "自定义风格", builtin_override: "内置覆盖" } as const)[origin];
}
